'use client';

import Link from 'next/link';

import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { commentaryService } from '@/lib/services/commentaryService';

export default function BibleReaderMain({
  verses,
  bookInfo,
  selectedChapter,
  selectedVersion,
  selectedLanguage,
  viewMode,
  showCrossRefs,
  showCommentary,
  loading,
  searchQuery,
  highlights,
  selectedHighlightColor,
  onVerseClick,
  onCrossRefClick,
  onToggleLayout,
  onHighlightVerse,
  onChapterChange
}) {
  // STATE: Track commentary for all verses
  const [versesWithCommentary, setVersesWithCommentary] = useState({});
  const [loadingCommentary, setLoadingCommentary] = useState(false);

  // Load commentary for all verses when verses change
  useEffect(() => {
    const loadCommentaryForVerses = async () => {
      if (!verses || verses?.length === 0) return;
      if (!showCommentary) return;
      if (!bookInfo?.bookCode) return;
      
      setLoadingCommentary(true);
      const commentaryMap = {};
      
      try {
        // UPDATED: Use book code and chapter/verse numbers directly instead of verse ID lookup
        await Promise.all(
          verses?.map(async (verse) => {
            try {
              // Use the simplified location-based lookup
              const commentary = await commentaryService?.getCommentaryByLocation(
                bookInfo?.bookCode,
                selectedChapter,
                verse?.verse
              );
              
              if (commentary) {
                commentaryMap[verse?.verse] = commentary;
              }
            } catch (error) {
              console.error(`Error loading commentary for verse ${verse?.verse}:`, error);
            }
          })
        );
        
        setVersesWithCommentary(commentaryMap);
      } catch (error) {
        console.error('Error loading verse commentary:', error);
      } finally {
        setLoadingCommentary(false);
      }
    };
    
    loadCommentaryForVerses();
  }, [verses, showCommentary, bookInfo?.bookCode, selectedChapter]);

  const filteredVerses = searchQuery
    ? verses?.filter(v => v?.text?.toLowerCase()?.includes(searchQuery?.toLowerCase()))
    : verses;

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-12">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
        <p className="text-center text-muted-foreground mt-4">Loading scripture...</p>
      </div>
    );
  }

  if (!verses?.length) {
    return (
      <div className="bg-card rounded-lg border border-border p-12 text-center">
        <svg className="w-16 h-16 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
        <h3 className="text-lg font-semibold mb-2">No Content Available</h3>
        <p className="text-muted-foreground">
          Scripture content for this chapter is not yet available in the mock data.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm">
      {/* Chapter Header */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold">
            {bookInfo?.name} {selectedChapter}
          </h1>
          <Link
            href={`/verse/${bookInfo?.id}/${selectedChapter}/1`}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Full Verse Commentary
          </Link>
        </div>

        {/* Bible Text */}
        <div className="p-6 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* Verses */}
              <div className="space-y-4">
                {filteredVerses?.map((verse) => {
                  const verseKey = `${bookInfo?.bookCode}-${selectedChapter}-${verse?.verse}`;
                  const highlight = highlights?.[verseKey];
                  const hasCommentary = versesWithCommentary?.[verse?.verse];

                  return (
                    <div key={verse?.verse} className="group">
                      {/* Verse Content */}
                      <div
                        onClick={() => onVerseClick(verse?.verse)}
                        onDoubleClick={() => onHighlightVerse(verseKey, selectedHighlightColor)}
                        className={`flex gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                          highlight
                            ? 'bg-opacity-20' :'hover:bg-accent/5'
                        }`}
                        style={highlight ? { backgroundColor: getHighlightColor(highlight) } : {}}
                      >
                        <span className="flex-shrink-0 text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded">
                          {verse?.verse}
                        </span>
                        <p className="flex-1 text-sm leading-relaxed text-foreground font-source-serif">
                          {verse?.text}
                        </p>
                        
                        {/* Commentary Indicator */}
                        {hasCommentary && showCommentary && (
                          <div className="flex-shrink-0 flex items-start">
                            <span className="inline-flex items-center gap-1 text-xs text-accent bg-accent/10 px-2 py-1 rounded">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                              </svg>
                              Commentary
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Show Commentary Below Verse if Available */}
                      {hasCommentary && showCommentary && (
                        <div className="ml-12 mt-2 p-3 bg-accent/5 border-l-2 border-accent rounded-r-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                            </svg>
                            <span className="text-xs font-medium text-accent">
                              {hasCommentary?.type === 'verse' ? 'Verse' : 
                               hasCommentary?.type === 'chapter' ? 'Chapter' : 'Book'} Commentary
                            </span>
                          </div>
                          <div className="text-sm leading-relaxed text-foreground/90 font-source-serif">
                            {hasCommentary?.commentary?.content?.split('\n')?.slice(0, 2)?.map((para, idx) => (
                              <p key={idx} className="mb-1 last:mb-0">
                                {para?.length > 200 ? `${para?.substring(0, 200)}...` : para}
                              </p>
                            ))}
                            {hasCommentary?.commentary?.content?.split('\n')?.length > 2 && (
                              <button
                                onClick={() => onVerseClick(verse?.verse)}
                                className="text-xs text-primary hover:underline mt-2"
                              >
                                Read full commentary →
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Commentary Loading Indicator */}
              {loadingCommentary && showCommentary && (
                <div className="text-center py-2">
                  <span className="text-xs text-muted-foreground">Loading commentary...</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Chapter Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => selectedChapter > 1 && onChapterChange?.(selectedChapter - 1)}
          disabled={selectedChapter <= 1}
          className="flex items-center gap-2 px-6 py-3 bg-accent text-accent-foreground rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Previous Chapter
        </button>

        <button
          onClick={() => onChapterChange?.(selectedChapter + 1)}
          disabled={selectedChapter >= (bookInfo?.chapters || 1)}
          className="flex items-center gap-2 px-6 py-3 bg-accent text-accent-foreground rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          Next Chapter
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// Helper function for highlight colors
function getHighlightColor(colorName) {
  const colors = {
    yellow: '#fef08a',
    green: '#86efac',
    blue: '#93c5fd',
    pink: '#f9a8d4',
    purple: '#d8b4fe',
    orange: '#fdba74',
    red: '#fca5a5',
    teal: '#5eead4'
  };
  return colors?.[colorName] || colors?.yellow;
}

BibleReaderMain.propTypes = {
  verses: PropTypes?.array?.isRequired,
  bookInfo: PropTypes?.object,
  selectedChapter: PropTypes?.number?.isRequired,
  selectedVersion: PropTypes?.string?.isRequired,
  selectedLanguage: PropTypes?.string?.isRequired,
  viewMode: PropTypes?.string?.isRequired,
  showCrossRefs: PropTypes?.bool?.isRequired,
  showCommentary: PropTypes?.bool?.isRequired,
  loading: PropTypes?.bool?.isRequired,
  searchQuery: PropTypes?.string?.isRequired,
  highlights: PropTypes?.object?.isRequired,
  selectedHighlightColor: PropTypes?.string?.isRequired,
  onVerseClick: PropTypes?.func?.isRequired,
  onCrossRefClick: PropTypes?.func?.isRequired,
  onHighlightVerse: PropTypes?.func?.isRequired,
  onChapterChange: PropTypes?.func?.isRequired
};